import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";

import type { UserPermissions } from "@/core/auth";
import { joinList } from "utils/joinList";

const THIRD_PARTY_NOISE_HOSTS = [
  "api.segment.io",
  "fast.appcues.com",
  "gist.build",
  "roadmap.repeatmd.app",
];
const THIRD_PARTY_NOISE_STACK_SIGNATURES = ["ProductLiftWidget", "ProductLiftIdentify"];

const SIGNALR_NOTIFICATIONS_HUB_PATH = "/api/hubs/notifications";

/** The login-free Done-For-You opt-in page, whose email link carries its bearer token in the fragment. */
const DFY_OPT_IN_LINK_PATH = "/dfy-opt-in";

const SIGNALR_NEGOTIATION_MARKERS = [
  "failed to start the connection",
  "failed to complete negotiation",
] as const;

const isTransientSignalRTransportMessage = (message: string): boolean =>
  message.includes("handshake was canceled") ||
  message.includes("message is incomplete") ||
  message.includes("connection was stopped") ||
  message.includes("aborted") ||
  message.includes("failed to fetch") ||
  message.includes("networkerror when attempting to fetch resource") ||
  message.includes("load failed") ||
  message.includes("bad gateway") ||
  message.includes("gateway time-out") ||
  message.includes("a timeout occurred") ||
  /status code '5\d{2}'/.test(message);

const stripUncaughtWrapper = (message: string): string =>
  message.replace(/^uncaught(?:\s+\(in promise\))?\s+/i, "").trim();

const firstMessageLine = (message: string | undefined): string =>
  (message ?? "").split("\n")[0]?.trim().toLowerCase() ?? "";

const isGenericBrowserFetchFailure = (message: string): boolean => {
  const normalized = stripUncaughtWrapper(message.trim().toLowerCase());
  return (
    normalized === "networkerror when attempting to fetch resource." ||
    normalized === "networkerror when attempting to fetch resource" ||
    normalized === "load failed" ||
    normalized === "failed to fetch" ||
    normalized === "typeerror: failed to fetch" ||
    normalized === "typeerror: load failed" ||
    normalized === "typeerror: networkerror when attempting to fetch resource."
  );
};

const hasSignalRTransportContext = (
  resourceUrl: string | undefined,
  ...messages: (string | undefined)[]
): boolean => {
  if (resourceUrl?.includes(SIGNALR_NOTIFICATIONS_HUB_PATH)) {
    return true;
  }
  const combined = messages
    .filter((message): message is string => Boolean(message))
    .join("\n")
    .toLowerCase();
  return SIGNALR_NEGOTIATION_MARKERS.some((marker) => combined.includes(marker));
};

export const isIgnorableSignalRTransportNoise = (
  resourceUrl: string | undefined,
  errorMessage: string | undefined,
  additionalContext?: string,
): boolean => {
  const combinedMessage = [errorMessage, additionalContext]
    .filter((message): message is string => Boolean(message))
    .join("\n");
  const normalizedMessage = combinedMessage.toLowerCase();
  if (!isTransientSignalRTransportMessage(normalizedMessage)) {
    return false;
  }
  if (hasSignalRTransportContext(resourceUrl, errorMessage, additionalContext)) {
    return true;
  }
  if (resourceUrl) {
    return false;
  }
  return [firstMessageLine(errorMessage), firstMessageLine(additionalContext)].some(
    (line) => line.length > 0 && isGenericBrowserFetchFailure(line),
  );
};

type NetworkTransportEventFields = {
  origin: string;
  statusCode: number | undefined;
  url: string | undefined;
  isAborted: boolean;
};

export const isIgnorableNetworkTransportEvent = ({
  origin,
  statusCode,
  url,
  isAborted,
}: NetworkTransportEventFields): boolean => {
  if (origin !== "network") {
    return false;
  }
  if (isAborted) {
    // App cancelled its own request (SignalR stop/unload/reconnect).
    return true;
  }
  return statusCode === 0 && Boolean(url?.includes(SIGNALR_NOTIFICATIONS_HUB_PATH));
};

export const isThirdPartyNoiseStack = (stack: string | undefined): boolean => {
  if (!stack) return false;
  if (THIRD_PARTY_NOISE_STACK_SIGNATURES.some((sig) => stack.includes(sig))) {
    return true;
  }
  return THIRD_PARTY_NOISE_HOSTS.some((host) => stack.includes(host));
};

export const isThirdPartyNoiseUrl = (url: string | undefined): boolean => {
  if (!url) return false;
  return THIRD_PARTY_NOISE_HOSTS.some((host) => url.includes(host));
};

/**
 * `url` without the opt-in link's fragment when it is the Done-For-You opt-in page. RUM records the
 * landing URL before the page takes the token out of the address bar, so the token would otherwise
 * reach Datadog verbatim.
 */
export const withoutDfyOptInLinkToken = (url: string): string => {
  const parsed = new URL(url);
  return parsed.pathname === DFY_OPT_IN_LINK_PATH
    ? parsed.origin + parsed.pathname + parsed.search
    : url;
};

/**
 * Initialize Datadog [Real User Monitoring](https://docs.datadoghq.com/real_user_monitoring/)
 */
export const initDatadog = () => {
  if (!import.meta.env.REACT_APP_DD_ENV) {
    console.warn("Datadog RUM not initialized: REACT_APP_DD_ENV is not set");
    return;
  }

  datadogRum.init({
    applicationId: "1262676b-3ade-4b37-83d7-704893c3dbaa",
    clientToken: "pub747d87ce0afe3e02dfafc0f0f2d21711",
    site: "datadoghq.com",
    service: "admin-web",
    env: import.meta.env.REACT_APP_DD_ENV,
    version: import.meta.env.REACT_APP_VERSION,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    allowedTracingUrls: [location.origin],
    sessionPersistence: "local-storage",
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: "allow",
    beforeSend: (event) => {
      event.view.url = withoutDfyOptInLinkToken(event.view.url);
      if (event.view.referrer) {
        event.view.referrer = withoutDfyOptInLinkToken(event.view.referrer);
      }
      if (event.type === "resource") {
        event.resource.url = withoutDfyOptInLinkToken(event.resource.url);
      }
      if (event.type === "error") {
        if (isThirdPartyNoiseStack(event.error.stack)) {
          return false;
        }
        if (isThirdPartyNoiseUrl(event.error.resource?.url)) {
          return false;
        }
        if (
          isIgnorableSignalRTransportNoise(
            event.error.resource?.url,
            event.error.message,
            event.error.stack,
          )
        ) {
          return false;
        }
      }
      if (event.type === "resource" && isThirdPartyNoiseUrl(event.resource.url)) {
        return false;
      }
      return true;
    },
  });

  datadogLogs.init({
    clientToken: "pub747d87ce0afe3e02dfafc0f0f2d21711",
    service: "admin-web",
    site: "datadoghq.com",
    env: import.meta.env.REACT_APP_DD_ENV,
    version: import.meta.env.REACT_APP_VERSION,
    sessionSampleRate: 100,
    sessionPersistence: "local-storage",
    forwardErrorsToLogs: true,
    forwardConsoleLogs: ["error"],
    beforeSend: (log, context) => {
      // Logs carry the page's URL too, which on the opt-in page can still hold the link's token.
      log.view.url = withoutDfyOptInLinkToken(log.view.url);
      if (log.view.referrer) {
        log.view.referrer = withoutDfyOptInLinkToken(log.view.referrer);
      }
      if (
        isIgnorableNetworkTransportEvent({
          origin: log.origin,
          statusCode: log.http?.status_code,
          url: log.http?.url,
          // Domain-context type is a union; only the network shape carries isAborted.
          isAborted: Boolean(context && "isAborted" in context && context.isAborted),
        })
      ) {
        return false;
      }
      if (isThirdPartyNoiseStack(log.error?.stack)) {
        return false;
      }
      if (isThirdPartyNoiseUrl(log.http?.url)) {
        return false;
      }
      const logMessage = typeof log.message === "string" ? log.message : undefined;
      if (
        isIgnorableSignalRTransportNoise(
          log.http?.url,
          log.error?.message ?? logMessage,
          logMessage,
        )
      ) {
        return false;
      }
      return true;
    },
  });

  datadogRum.setGlobalContextProperty("origin", location.origin);
  datadogLogs.setGlobalContextProperty("origin", location.origin);
};

type DatadogUser = {
  id: string;
  name?: string;
  email?: string;
  permissions: UserPermissions;
  platform?: { id: string; name: string };
};

/**
 * Set or update the user context for the active Datadog RUM session
 */
export const setDatadogUser = (user: DatadogUser) => {
  datadogRum.setUser({
    id: user.id,
    name: user.name,
    email: user.email,
    userRoles: joinList(user.permissions.roles),
    scopedPlatformId: user.permissions.platformId,
    platformId: user.platform?.id,
    platformName: user.platform?.name,
  });
};

/**
 * Clear the user context from the active Datadog RUM session (e.g. on logout).
 */
export const clearDatadogUser = () => {
  try {
    datadogRum.clearUser();
  } catch {
    // RUM may not be initialized; avoid breaking caller
  }
};

/**
 * Report an error to Datadog RUM. No-op if RUM is not initialized (e.g. missing env).
 * Use from error boundaries or other error handlers so all RUM usage stays in this module.
 */
export const addDatadogError = (error: Error, context?: Record<string, unknown>): void => {
  try {
    datadogRum.addError(error, context);
  } catch {
    // RUM may not be initialized; avoid breaking caller
  }
};
